import React from "react";

import _ from "lodash";

import { CheckErrorVisibilityInterface } from "commons/types/form";

import Tooltip from "commons/ui/Tooltip";

export type PropsType = {
    [x: string]: any;
    label?: string;
    errors?: string[];
    onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onKeyDown?: (e: any) => boolean;
    setValue?: (path: string, value: any) => void;
    hideError?: (path: string) => void;
    showError?: (path: string) => void;
    path?: string;
    min?: number;
    onClick?: CallableFunction;
    startAdornment?: any;
    endAdornment?: any;
    childrenAsStartAdornment?: boolean;
    childrenAsEndAdornment?: boolean;
    children?: any;
    textarea?: boolean;
    largeBox?: boolean;
    tooltip?: any;
    type?: "text" | "password";
    disable?: boolean;
    disableOnlyInput?: boolean;
    className?: string;
    name?: string;
    ctaElement?: JSX.Element;
    autoComplete?: string;
    placeholder?: string;
    tabIndex?: number;
    autoFocus?: boolean;
    filterInput?: boolean;
    maxLength?: number;
    required?: boolean;
    horizontalLabel?: boolean;
    dontShowObligatoryFlag?: boolean;
    checkErrorVisibility?: CheckErrorVisibilityInterface;
};

const calcInputFunctionProps = (props: PropsType, setFocused: CallableFunction) => {
    const onFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFocused(true);

        if (props.onFocus) {
            props.onFocus(event);
        } else if (props.hideError && props.path) {
            props.hideError(props.path);
        }
    };

    const onBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFocused(false);

        if (props.onBlur) {
            props.onBlur(event);
        } else if (props.showError && props.path) {
            props.showError(props.path);
        }
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (props.onChange) {
            props.onChange(event);
        } else if (props.setValue && props.path) {
            props.setValue(props.path, event.target.value);
        }
    };

    return {
        onFocus,
        onBlur,
        onChange,
    };
};

const calcInputProps = (props: PropsType, setFocused: CallableFunction) => {
    const inputProps: any = calcInputFunctionProps(props, setFocused);

    if (props.readonly) {
        inputProps.readOnly = true;
    }

    if (props.disabled || props.disableOnlyInput) {
        inputProps.disabled = true;
    }

    if (props.placeholder) {
        inputProps.placeholder = props.placeholder;
    }

    if (props.name) {
        inputProps.name = props.name;
    }

    if (props.autoComplete) {
        inputProps.autoComplete = props.autoComplete;
    } else if (props.filterInput) {
        inputProps.autoComplete = "new-password";
    }

    return inputProps;
};

function getInput(props: PropsType, inputProps: any, ref: any, errorsId: string) {
    if (props.textarea) {
        return (
            <textarea
                className={props.largeBox ? "large-box" : ""}
                rows={2}
                ref={ref}
                value={props.value ?? ""}
                aria-label={props.label ?? ""}
                data-testid={"input-text-textarea"}
                tabIndex={props.tabIndex}
                aria-invalid={props.errors && props.errors.length > 0 ? true : undefined}
                aria-required={props.required ? true : undefined}
                aria-describedby={errorsId}
                autoFocus={props.autoFocus}
                {...inputProps}
            />
        );
    } else {
        return (
            <input
                type={props.type ?? "text"}
                data-testid={"input-text"}
                value={props.value ?? ""}
                aria-label={props.label ?? ""}
                ref={ref}
                tabIndex={props.tabIndex}
                autoFocus={props.autoFocus}
                onKeyDown={props.onKeyDown}
                min={props.min}
                maxLength={props.maxLength}
                aria-invalid={props.errors && props.errors.length > 0 ? true : undefined}
                aria-required={props.required ? true : undefined}
                aria-describedby={errorsId}
                {...inputProps}
            />
        );
    }
}

const calculateClasses = (props: PropsType, focused: boolean): Array<string> => {
    const classes = ["input"];

    if (props.errors && props.errors.length > 0) {
        classes.push("has-error");
    }

    if (focused) {
        classes.push("focused");
    }

    if (props.filterInput && props.value) {
        classes.push("not-empty");
    }

    if (props.className) {
        classes.push(props.className);
    }

    if (props.value) {
        classes.push("has-value");
    }

    return classes;
};

const InputText = React.forwardRef((props: PropsType, ref) => {
    const [focused, setFocused] = React.useState(false);

    const errors: Array<string> = [];

    if (props.checkErrorVisibility && props.path) {
        props.checkErrorVisibility(props.path).forEach((error) => errors.push(error));
    } else if (_.isArray(props.errors)) {
        props.errors.forEach((error) => errors.push(error));
    }

    const classes = calculateClasses(
        {
            ...props,
            errors: errors,
        },
        focused
    );

    let comboboxProps = {};

    if (props.getComboboxProps) {
        comboboxProps = props.getComboboxProps();
    }

    const inputProps = calcInputProps(props, setFocused);

    const errorsId: string = React.useMemo(() => {
        if (props.label) {
            return `${props.label.toLocaleLowerCase().replace(/\s/g, "")}-input-text-errors`;
        }

        return `${_.uniqueId()}-input-text-errors`;
    }, [props.label]);

    return (
        <div
            className={`${classes.join(" ")} ${props.horizontalLabel ? "horizontal-input" : ""}`}
            {...comboboxProps}
        >
            <div className={"label-cta-container"}>
                <div className={"label-container"}>
                    {props.label && (
                        <label>
                            {props.label}
                            {props.required && !props.dontShowObligatoryFlag ? (
                                <span aria-describedby={"obligatory-fields-info-description"}>
                                    *
                                </span>
                            ) : null}
                        </label>
                    )}

                    {props.tooltip && <Tooltip tooltip={props.tooltip} />}
                </div>

                {props.ctaElement && <div className={"cta-container"}>{props.ctaElement}</div>}
            </div>

            <div
                className={
                    (props.textarea ? " input-container-textarea " : " input-container ") +
                    (props.disabled ? " disabled " : "")
                }
            >
                {props.startAdornment && <div>{props.startAdornment}</div>}
                {props.childrenAsStartAdornment && <div>{props.children}</div>}
                <div
                    className={"input-div"}
                    onClick={(event: any) => {
                        if (props.onClick) {
                            props.onClick(event);
                        }
                    }}
                >
                    {getInput(
                        {
                            ...props,
                            errors: errors,
                        },
                        inputProps,
                        ref,
                        errorsId
                    )}
                </div>
                {props.endAdornment && <div>{props.endAdornment}</div>}
                {props.childrenAsEndAdornment && <div>{props.children}</div>}
            </div>

            <span id={errorsId} className={"errors"}>
                {errors ? errors.join(", ") : ""}
            </span>
        </div>
    );
});

export default InputText;
